import {
  getBagSummary,
  getBag,
  getBagById,
  subscribeToUpdates,
  removeItem,
  startCheckout
} from "@src/client/sdk/bag";
import {
  BAG_ERROR,
  BAG_ITEM_DELETE_TRANSITION_END,
  FINISH_REMOVE_BAG_ITEM,
  getBagItems,
  getTotalQuantity,
  SET_BAG,
  SET_LOADING,
  START_CHECKOUT,
  START_REMOVE_BAG_ITEM
} from "@template/state/modules/bag";
import {
  SET_NOTIFICATION,
  HIDE_NOTIFICATION,
  getNotificationData,
  getMinibagNotification,
  MINIBAG as MINIBAG_NOTIFICATION
} from "@template/state/modules/notifications";
import {
  OPEN_DROPDOWN,
  MINIBAG,
  getDropdownIsOpen,
  CLOSE_DROPDOWN
} from "@template/state/modules/dropdown";
import { getSecureStoreUrl } from "@template/state/modules/regionalStore";
import { withParams } from "@src/helpers/queryString";
import miniBagEnabled from "@template/state/middleware/bag/helpers/miniBagEnabled";
import bagWithProductLinks from "@template/state/middleware/bag/helpers/bagWithProductLinks";
import pageSupportsMiniBagWithDropdown from "@template/helpers/pageSupportsMiniBagWithDropdown";
import { getBagCheckoutPromptMVTStatus } from "@template/state/modules/infoBanner";
import {
  consumeSizeChangeFlag,
  setSizeChangeFlag
} from "@src/client/analytics/dataLayerEvents/bagUpdateTracker";
import { eventBus } from "@src/helpers/eventing/events";
import {
  computeBagDiff,
  determineCartAction,
  DEFERRED_REMOVAL_TIMEOUT_MS
} from "@template/state/middleware/bag/helpers/cartActionHelpers";

const triggerCartEvent = (bag, bagId, action) => {
  if (!bag || !bagId) return;

  import("@src/client/analytics/dataLayerEvents/triggerCartUpdatedEvent")
    .then(({ triggerCartUpdatedEvent }) => {
      triggerCartUpdatedEvent(bag, bagId, action);
    })
    .catch(() => {});
};

const handleRemovedItems = (
  state,
  current,
  bag,
  newQuantity,
  messages,
  getState,
  dispatch
) => {
  const items = current;

  if (!pageSupportsMiniBagWithDropdown(window.location.pathname)) {
    dispatch({
      type: SET_BAG,
      payload: {
        bag: {
          ...bagWithProductLinks({ ...state.bag, items }, getState),
          incoming: bag,
          isUpdated: true,
          summary: {
            ...state.bag.summary,
            totalQuantity: newQuantity
          }
        },
        messages
      }
    });
  } else {
    dispatch({
      type: SET_BAG,
      payload: {
        bag: {
          ...bagWithProductLinks({ ...state.bag, items }, getState),
          incoming: bag,
          isUpdated: true
        },
        messages
      }
    });
  }

  const bagId = bag.summary?.bagId || state.bag?.bagId;
  return bagId;
};

const shouldTriggerCartUpdate = (
  isFirstCallback,
  state,
  next,
  itemsWereAdded,
  expectingBagUpdate,
  newQuantity,
  oldQuantity,
  current
) => {
  const stateHasItems = state.bag?.items && state.bag.items.length > 0;
  const isInitialLoadWithExistingItems =
    isFirstCallback && !stateHasItems && next.length > 0 && !itemsWereAdded;
  const hasRealChange =
    !isInitialLoadWithExistingItems &&
    (expectingBagUpdate ||
      itemsWereAdded ||
      (stateHasItems &&
        (newQuantity !== oldQuantity || next.length !== current.length)));

  return hasRealChange;
};

const openMinibagWithNotification = (
  bag,
  sellerChanged,
  containsHotProduct,
  dispatch
) => {
  const firstItemType = bag.items?.[0]?.itemType || "Product";

  dispatch({
    type: SET_NOTIFICATION,
    payload: {
      notificationType: MINIBAG_NOTIFICATION,
      notificationData: {
        isVisible: true,
        ...getNotificationData({
          sellerChanged,
          containsHotProduct,
          itemType: firstItemType
        })
      }
    }
  });

  dispatch({
    type: OPEN_DROPDOWN,
    name: MINIBAG,
    programmatic: true
  });
};

const handleNormalUpdate = (bag, getState, dispatch, messages) => {
  dispatch({
    type: SET_BAG,
    payload: {
      bag: {
        ...bagWithProductLinks(bag, getState),
        isUpdated: true
      },
      messages
    }
  });
};

const bagMiddleware = ({ getState, dispatch }) => {
  let expectingBagUpdate = false;
  let isFirstCallback = true;
  let previousBag = null;
  let pendingRemovals = [];
  let pendingRemovalTimeout = null;

  const resolvePendingRemoval = action => {
    if (pendingRemovals.length) {
      const removals = pendingRemovals;
      pendingRemovals = [];
      if (pendingRemovalTimeout) {
        clearTimeout(pendingRemovalTimeout);
        pendingRemovalTimeout = null;
      }
      removals.forEach((removal, index) => {
        const resolvedAction =
          index === removals.length - 1 ? action : "item_removed";
        triggerCartEvent(removal.bag, removal.bagId, resolvedAction);
      });
    }
  };

  const fireOrDeferCartEvent = (bag, bagId, cartAction) => {
    if (cartAction === "item_removed") {
      if (pendingRemovalTimeout) clearTimeout(pendingRemovalTimeout);
      pendingRemovals.push({ bag, bagId });
      pendingRemovalTimeout = setTimeout(
        () => resolvePendingRemoval("item_removed"),
        DEFERRED_REMOVAL_TIMEOUT_MS
      );
    } else {
      resolvePendingRemoval("item_removed");
      triggerCartEvent(bag, bagId, cartAction);
    }
  };

  eventBus.addListener("tracking.asosBag.updateItem", data => {
    if (data && data.updateType === "size") {
      setSizeChangeFlag();
    }
  });
  eventBus.addListener("tracking.asosBag.saveForLater", () => {
    resolvePendingRemoval("add_to_wishlist");
  });

  if (typeof getBagSummary === "function") {
    getBagSummary()
      .then(summary => {
        if (summary) {
          dispatch({
            type: SET_BAG,
            payload: { bag: { summary, bagId: summary.bagId } }
          });
        }
      })
      .catch(() => {});
  }

  subscribeToUpdates(({ bag, messages, sellerChanged, containsHotProduct }) => {
    const state = getState();
    const current =
      (previousBag && getBagItems({ bag: previousBag })) ||
      getBagItems(state) ||
      [];
    previousBag = bag;
    const next = getBagItems({ bag });
    const oldQuantity = getTotalQuantity(state);
    const newQuantity = getTotalQuantity({ bag });

    const {
      removed,
      itemsWereAdded,
      itemsWereSwapped,
      quantityChanged,
      itemSizeChanged
    } = computeBagDiff(current, next, oldQuantity, newQuantity);

    const sizeChangeResult = consumeSizeChangeFlag();

    const cartAction = determineCartAction(
      sizeChangeResult || itemsWereSwapped,
      quantityChanged,
      newQuantity,
      oldQuantity,
      itemsWereAdded,
      itemSizeChanged
    );

    const miniBagIsEnabled = miniBagEnabled(state);

    if (miniBagIsEnabled && removed.length) {
      handleRemovedItems(
        state,
        current,
        bag,
        newQuantity,
        messages,
        getState,
        dispatch
      );

      const bagId = bag.summary?.bagId || state.bag?.bagId;
      if (bagId) {
        fireOrDeferCartEvent(bag, bagId, cartAction);
      }

      expectingBagUpdate = false;
      isFirstCallback = false;
      return;
    }

    handleNormalUpdate(bag, getState, dispatch, messages);

    const hasItemLevelChanges =
      itemsWereSwapped || sizeChangeResult || itemSizeChanged;

    const hasBagLevelChanges = shouldTriggerCartUpdate(
      isFirstCallback,
      state,
      next,
      itemsWereAdded,
      expectingBagUpdate,
      newQuantity,
      oldQuantity,
      current
    );

    const shouldFireCartEvent = hasBagLevelChanges || hasItemLevelChanges;

    if (shouldFireCartEvent) {
      const bagId = bag.summary?.bagId || state.bag?.bagId;
      if (bagId) {
        fireOrDeferCartEvent(bag, bagId, cartAction);
      }
    }
    expectingBagUpdate = false;
    isFirstCallback = false;

    if (itemsWereAdded && miniBagIsEnabled) {
      if (!pageSupportsMiniBagWithDropdown(window.location.pathname)) {
        openMinibagWithNotification(
          bag,
          sellerChanged,
          containsHotProduct,
          dispatch
        );

        return;
      }

      if (bag.delivery?.options) {
        openMinibagWithNotification(
          bag,
          sellerChanged,
          containsHotProduct,
          dispatch
        );

        return;
      }

      const bagId = bag.id || bag.summary?.bagId || state.bag?.bagId;

      getBagById(bagId)
        .then(({ bag: enrichedBag, messages: enrichedMessages }) => {
          dispatch({
            type: SET_BAG,
            payload: {
              bag: {
                ...bagWithProductLinks(enrichedBag, getState),
                isUpdated: true
              },
              messages: enrichedMessages
            }
          });

          openMinibagWithNotification(
            enrichedBag,
            sellerChanged,
            containsHotProduct,
            dispatch
          );
        })
        .catch(() => {
          openMinibagWithNotification(
            bag,
            sellerChanged,
            containsHotProduct,
            dispatch
          );
        });
    }
  });

  return next => action => {
    const state = getState();

    if (!miniBagEnabled(state) && !getBagCheckoutPromptMVTStatus(state)) {
      next(action);
      return;
    }

    if (action.type === START_REMOVE_BAG_ITEM) {
      dispatch({
        type: "DEFER_ACTIONS",
        payload: {
          actionsToDefer: [OPEN_DROPDOWN],
          actionToWaitFor: BAG_ITEM_DELETE_TRANSITION_END,
          actionsToCancelOn: [BAG_ERROR]
        }
      });

      const items = getBagItems(state);
      const item = items.find(({ id }) => id === action.item);

      expectingBagUpdate = true;

      removeItem(item.id, item.itemType.toLowerCase())
        .then(() => {
          dispatch({ type: FINISH_REMOVE_BAG_ITEM, itemId: action.item });
        })
        .catch(() => {
          expectingBagUpdate = false;
          dispatch({ type: BAG_ERROR });
        });
    }

    if (
      action.type === OPEN_DROPDOWN &&
      action.name === MINIBAG &&
      !action.programmatic
    ) {
      const miniBagDropdownIsOpen = getDropdownIsOpen(state, { name: MINIBAG });

      if (!miniBagDropdownIsOpen) {
        dispatch({ type: SET_LOADING });
        getBag()
          .then(({ bag, messages }) => {
            dispatch({
              type: SET_BAG,
              payload: {
                bag: bagWithProductLinks(bag, getState),
                messages
              }
            });

            if (bag.items.length) {
              dispatch({
                type: SET_NOTIFICATION,
                payload: {
                  notificationType: MINIBAG_NOTIFICATION,
                  notificationData: {
                    ...getMinibagNotification(getState())
                  }
                }
              });
            }
          })
          .catch(() => dispatch({ type: BAG_ERROR }));
      }
    }

    if (action.type === CLOSE_DROPDOWN && action.name === MINIBAG) {
      dispatch({
        type: HIDE_NOTIFICATION,
        payload: {
          notificationType: MINIBAG_NOTIFICATION
        }
      });

      const incoming = state.bag.incoming;

      if (incoming && incoming.items.length === 0) {
        dispatch({
          type: SET_BAG,
          payload: {
            bag: bagWithProductLinks(
              { ...incoming, incoming: undefined },
              getState
            )
          }
        });
      }
    }

    if (
      action.type === BAG_ITEM_DELETE_TRANSITION_END &&
      action.itemId === state.bag.lastItemRemoved
    ) {
      const incoming = state.bag.incoming;
      dispatch({
        type: SET_BAG,
        payload: {
          bag: bagWithProductLinks(
            { ...incoming, incoming: undefined },
            getState
          )
        }
      });
      dispatch({
        type: HIDE_NOTIFICATION,
        payload: {
          notificationType: MINIBAG_NOTIFICATION
        }
      });
    }

    if (action.type === START_CHECKOUT) {
      const state = getState();
      const checkoutLink = getSecureStoreUrl(state);
      startCheckout()
        .then(() => {
          const checkoutUrl = checkoutLink + "initialise";
          window.location.replace(
            withParams(checkoutUrl, { ctaref: "mini bag" })
          );
        })
        .catch(() => {});
    }

    return next(action);
  };
};

export default bagMiddleware;
